import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import ArrowBackIcon from '@mui/icons-material/ArrowBackIos'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import LoadingButton from '@mui/lab/LoadingButton'
import { Typography, useTheme } from '@mui/material'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog, { DialogProps } from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'

import { Color } from '../../../@types/button'

interface Props extends DialogProps {
  closeDialog: () => void
  title?: string
  titleIcon?: ReactNode
  onSubmit?: () => void
  backAction?: () => void
  disabledSubmit?: boolean
  cancelLabel?: string
  submitLabel?: string
  disabledAction?: boolean
  disabledTitle?: boolean
  disabledCancelButton?: boolean
  enableCloseButton?: boolean
  enableBackButton?: boolean
  formName?: string
  submitBtnColor?: Color
  cancelBtnColor?: Color
  isSubmitLoading?: boolean
  titleAlign?: 'left' | 'center' | 'right'
  textSize?: 'h3' | 'h4' | 'h5'
  contentMinWidth?: number
  contentMinHeight?: number | string
  contentMaxWidth?: number
  contentMaxHeight?: number | string
  titleMarginTop?: number
  note?: ReactNode
  customActions?: ReactNode
  enableInLineWithTitle?: boolean
}
/**
 * Dialog with custom body, title, and actions
 */
const CustomDialog = ({
  title = 'Dialog',
  closeDialog,
  onSubmit,
  disabledSubmit,
  backAction,
  cancelLabel,
  submitLabel,
  disabledAction = false,
  disabledTitle = false,
  disabledCancelButton = false,
  formName = 'form-input',
  submitBtnColor = 'primary',
  cancelBtnColor = 'primary',
  isSubmitLoading = false,
  enableCloseButton = false,
  enableBackButton = false,
  enableInLineWithTitle = false,
  titleAlign = 'center',
  contentMinWidth = 300,
  contentMinHeight = 200,
  contentMaxWidth,
  contentMaxHeight,
  titleMarginTop,
  titleIcon,
  textSize = 'h4',
  note,
  customActions,
  ...props
}: Props) => {
  const theme = useTheme()
  const { t } = useTranslation('common')

  return (
    <Dialog {...props} onClose={closeDialog}>
      {!disabledTitle && !enableInLineWithTitle && (
        <DialogTitle sx={{ mb: 1, textAlign: titleAlign }}>
          <Stack flexDirection="row" justifyContent="space-between" sx={{ mt: titleMarginTop }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              {titleIcon && (
                <Box display="flex" justifyContent="center" alignItems="center">
                  {titleIcon}
                </Box>
              )}
              <Typography variant={textSize}>{title}</Typography>
            </Stack>
            <Box>{note}</Box>
          </Stack>
        </DialogTitle>
      )}

      {enableInLineWithTitle && (
        <DialogTitle sx={{ mb: 1, textAlign: titleAlign }}>
          <Stack
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mt: titleMarginTop }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              {titleIcon && (
                <Box display="flex" justifyContent="center" alignItems="center">
                  {titleIcon}
                </Box>
              )}
              <Typography variant={textSize}>{title}</Typography>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center">
              {note && <Box>{note}</Box>}
              {enableCloseButton && (
                <IconButton aria-label="close" onClick={closeDialog} size="small">
                  <CloseRoundedIcon fontSize="medium" />
                </IconButton>
              )}
            </Stack>
          </Stack>
        </DialogTitle>
      )}

      <DialogContent
        sx={{
          pb: 0,
          minWidth: contentMinWidth,
          minHeight: contentMinHeight,
          maxWidth: contentMaxWidth,
          maxHeight: contentMaxHeight,
        }}
      >
        <Box pt={2} pb={3}>
          {props.children}
        </Box>
      </DialogContent>
      {!disabledAction && (
        <DialogActions
          sx={{ display: 'flex', borderTop: `1px solid ${theme.palette.grey[400]}`, pt: 0 }}
        >
          <Stack spacing={1} direction="row">
            {!disabledCancelButton && (
              <Button
                onClick={closeDialog}
                color={cancelBtnColor}
                size="large"
                variant="outlined"
                sx={{ width: '50%', whiteSpace: 'nowrap', height: 'fit-content' }}
              >
                {cancelLabel || t('common:cancel')}
              </Button>
            )}
            <LoadingButton
              onClick={onSubmit}
              disabled={disabledSubmit}
              variant="contained"
              color={submitBtnColor}
              type={formName ? 'submit' : 'button'}
              size="large"
              form={formName}
              sx={{
                width: disabledCancelButton ? '100%' : '50%',
                height: 'fit-content',
                px: disabledCancelButton ? 5 : 0,
              }}
              loading={isSubmitLoading}
            >
              {submitLabel || t('common:confirm')}
            </LoadingButton>
            {customActions}
          </Stack>
        </DialogActions>
      )}
      {customActions && (
        <DialogActions
          sx={{
            display: 'flex',
            borderTop: `1px solid ${theme.palette.grey[400]}`,
            pt: 0,
          }}
        >
          {customActions}
        </DialogActions>
      )}
      {enableCloseButton && !enableInLineWithTitle && (
        <IconButton
          aria-label="close"
          onClick={closeDialog}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[600],
          }}
        >
          <CloseRoundedIcon fontSize="small" />
        </IconButton>
      )}
      {enableBackButton && (
        <IconButton
          aria-label="back"
          onClick={backAction}
          sx={{
            position: 'absolute',
            left: 8,
            top: 8,
            color: (theme) => theme.palette.grey[600],
          }}
        >
          <ArrowBackIcon fontSize="small" />
        </IconButton>
      )}
    </Dialog>
  )
}

export default CustomDialog
